import { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Select,
  MenuItem,
  Box,
  FormControl,
  Button,
  Menu,
  Chip,
  Divider,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MenuIcon from '@mui/icons-material/Menu';
import { trackEvent } from '@utils/analytics';
import { loadTranslation } from '@/i18n';

const languages = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'ru', name: 'Русский' },
  { code: 'pt', name: 'Português' },
];

const supportPages = [
  { path: '/support/windows', labelKey: 'nav.guidesWindows' },
  { path: '/support/macos', labelKey: 'nav.guidesMacos' },
  { path: '/support/linux', labelKey: 'nav.guidesLinux' },
];

const releasePages = [
  { path: '/releases/emoji-17', labelKey: 'nav.emoji170', isLatest: true },
  { path: '/releases/emoji-16', labelKey: 'nav.emoji160' },
  { path: '/releases/emoji-15-1', labelKey: 'nav.emoji151' },
  { path: '/releases/emoji-15', labelKey: 'nav.emoji150' },
  { path: '/releases/emoji-14', labelKey: 'nav.emoji140' },
  { path: '/releases/emoji-13-1', labelKey: 'nav.emoji131' },
  { path: '/releases/emoji-13', labelKey: 'nav.emoji130' },
];

export const Header = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const currentLangParam = new URLSearchParams(location.search).get('lang');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [guidesAnchorEl, setGuidesAnchorEl] = useState<null | HTMLElement>(null);
  const [releasesAnchorEl, setReleasesAnchorEl] = useState<null | HTMLElement>(null);
  const guidesOpen = Boolean(guidesAnchorEl);
  const releasesOpen = Boolean(releasesAnchorEl);

  const handleGuidesMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setGuidesAnchorEl(event.currentTarget);
  };

  const handleGuidesMenuClose = () => {
    setGuidesAnchorEl(null);
  };

  const handleReleasesMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setReleasesAnchorEl(event.currentTarget);
  };

  const handleReleasesMenuClose = () => {
    setReleasesAnchorEl(null);
  };

  const handlePageClick = (path: string, closeHandler: () => void) => {
    navigate(currentLangParam ? `${path}?lang=${currentLangParam}` : path);
    closeHandler();
  };

  const handleLanguageChange = async (event: { target: { value: string } }) => {
    const newLang = event.target.value;
    const oldLang = i18n.language;

    await loadTranslation(newLang);
    i18n.changeLanguage(newLang);
    localStorage.setItem('language', newLang);

    // Update the URL with the language parameter through React Router (not raw
    // history.pushState) so every useLocation() consumer (Footer, breadcrumbs,
    // menus) re-renders with the new ?lang= immediately.
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('lang', newLang);
    navigate(
      { pathname: location.pathname, search: `?${searchParams.toString()}`, hash: location.hash },
      { replace: false }
    );

    trackEvent('language_change', {
      event_category: 'engagement',
      event_label: newLang,
      previous_language: oldLang,
    });
  };

  const handleLogoClick = () => {
    navigate(currentLangParam ? `/?lang=${currentLangParam}` : '/');
  };

  return (
    <AppBar position="static" className="header">
      <Toolbar>
        {/* Logo and Site Name */}
        <Box
          component="a"
          href="/"
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            handleLogoClick();
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            cursor: 'pointer',
            textDecoration: 'none',
            color: 'inherit',
          }}
        >
          <img
            src="/logo-64.png"
            alt={t('nav.logoAlt')}
            style={{
              width: '40px',
              height: '40px',
            }}
          />
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            {t('appName')}
          </Typography>
        </Box>

        {/* Navigation Menu - positioned after site name with spacing */}
        <Box
          component="nav"
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            ml: { xs: 2, sm: 4 },
            gap: 1,
            flexGrow: 1,
          }}
        >
          {/* What's New / Releases Menu */}
          <Button
            id="whats-new-button"
            aria-controls={releasesOpen ? 'whats-new-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={releasesOpen ? 'true' : undefined}
            onClick={handleReleasesMenuOpen}
            endIcon={<KeyboardArrowDownIcon />}
            sx={{
              color: 'white',
              textTransform: 'none',
              fontSize: '0.95rem',
              fontWeight: 500,
              px: 2,
              py: 1,
              borderRadius: 1,
              border: '1px solid rgba(255, 255, 255, 0.3)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                borderColor: 'rgba(255, 255, 255, 0.5)',
              },
            }}
          >
            {t('nav.whatsNew')}
          </Button>
          <Menu
            id="whats-new-menu"
            anchorEl={releasesAnchorEl}
            open={releasesOpen}
            onClose={handleReleasesMenuClose}
            MenuListProps={{
              'aria-labelledby': 'whats-new-button',
            }}
          >
            {releasePages.map((page) => (
              <MenuItem
                key={page.path}
                component="a"
                href={currentLangParam ? `${page.path}?lang=${currentLangParam}` : page.path}
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  handlePageClick(page.path, handleReleasesMenuClose);
                }}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                {t(page.labelKey)}
                {page.isLatest && (
                  <Chip
                    label={t('nav.latest')}
                    size="small"
                    color="primary"
                    sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                  />
                )}
              </MenuItem>
            ))}
            <Divider />
            <MenuItem
              component="a"
              href={currentLangParam ? `/releases?lang=${currentLangParam}` : '/releases'}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                handlePageClick('/releases', handleReleasesMenuClose);
              }}
              sx={{ fontWeight: 500 }}
            >
              {t('nav.viewAllReleases')}
            </MenuItem>
          </Menu>

          {/* Emoji Guides Menu */}
          <Button
            id="emoji-guides-button"
            aria-controls={guidesOpen ? 'emoji-guides-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={guidesOpen ? 'true' : undefined}
            onClick={handleGuidesMenuOpen}
            endIcon={<KeyboardArrowDownIcon />}
            sx={{
              color: 'white',
              textTransform: 'none',
              fontSize: '0.95rem',
              fontWeight: 500,
              px: 2,
              py: 1,
              borderRadius: 1,
              border: '1px solid rgba(255, 255, 255, 0.3)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                borderColor: 'rgba(255, 255, 255, 0.5)',
              },
            }}
          >
            {t('nav.emojiGuides')}
          </Button>
          <Menu
            id="emoji-guides-menu"
            anchorEl={guidesAnchorEl}
            open={guidesOpen}
            onClose={handleGuidesMenuClose}
            MenuListProps={{
              'aria-labelledby': 'emoji-guides-button',
            }}
          >
            {supportPages.map((page) => (
              <MenuItem
                key={page.path}
                component="a"
                href={currentLangParam ? `${page.path}?lang=${currentLangParam}` : page.path}
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  handlePageClick(page.path, handleGuidesMenuClose);
                }}
              >
                {t(page.labelKey)}
              </MenuItem>
            ))}
            <Divider />
            <MenuItem
              component="a"
              href={currentLangParam ? `/support?lang=${currentLangParam}` : '/support'}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                handlePageClick('/support', handleGuidesMenuClose);
              }}
              sx={{ fontWeight: 500 }}
            >
              {t('nav.viewAllGuides')}
            </MenuItem>
          </Menu>
        </Box>

        {/* Mobile hamburger menu */}
        {isMobile && (
          <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton
              color="inherit"
              aria-label={t('a11y.openNavMenu')}
              onClick={() => setDrawerOpen(true)}
              sx={{ ml: 'auto' }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        )}
        <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
          <Box sx={{ width: 250 }} role="presentation">
            <List>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    handlePageClick('/', () => setDrawerOpen(false));
                  }}
                >
                  <ListItemText primary={t('footer.home')} />
                </ListItemButton>
              </ListItem>
              {releasePages.map((page) => (
                <ListItem key={page.path} disablePadding>
                  <ListItemButton
                    onClick={() => {
                      handlePageClick(page.path, () => setDrawerOpen(false));
                    }}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <ListItemText primary={t(page.labelKey)} sx={{ flexGrow: 0 }} />
                    {page.isLatest && (
                      <Chip
                        label={t('nav.latest')}
                        size="small"
                        color="primary"
                        sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
              {supportPages.map((page) => (
                <ListItem key={page.path} disablePadding>
                  <ListItemButton
                    onClick={() => {
                      handlePageClick(page.path, () => setDrawerOpen(false));
                    }}
                  >
                    <ListItemText primary={t(page.labelKey)} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </Drawer>

        {/* Language Selector - positioned at the right */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
            {t('languageLabel')}:
          </Typography>
          <FormControl size="small" sx={{ minWidth: { xs: 90, sm: 120 } }}>
            <Select
              value={i18n.language}
              onChange={handleLanguageChange}
              sx={{
                color: 'white',
                '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.7)',
                },
                '.MuiSvgIcon-root': { color: 'white' },
              }}
            >
              {languages.map((lang) => (
                <MenuItem key={lang.code} value={lang.code}>
                  {lang.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Toolbar>
    </AppBar>
  );
};
